import { Fragment, useEffect, useId, useRef, useState, type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { useApp } from '@/app/AppContext'
import { Icon, type IconName } from '@/components/Icon'
import '@/styles/record.css'

/**
 * Шапка карточки записи (CompRecord.dc.html, Deal.dc.html).
 *
 * Отвечает на «что это, с кем, что можно сделать» и держит **вес действий** макета:
 * одна основная кнопка, затем вторичные, затем «⋯». Опасные пункты меню (`danger`) всегда
 * уходят в его конец за черту — порядок задаёт компонент, а не каждая карточка заново:
 * иначе первая же карточка поставит «Удалить» рядом с «Скопировать ссылку».
 *
 * Права здесь не проверяются: действие без права вызывающий просто не передаёт. Кнопка,
 * нарисованная и отключённая, обещает возможность, которой у пользователя нет.
 */

export interface Crumb {
  label: ReactNode
  /** Нет адреса — последний сегмент, текущая запись. */
  to?: string
  mono?: boolean
}

export interface HeaderAction {
  id: string
  label: ReactNode
  onClick: () => void
  icon?: IconName
  disabled?: boolean
  /** Подсказка горячей клавиши (`.kbd`). Только если сочетание действительно работает. */
  kbd?: string
}

export interface MenuAction {
  id: string
  label: ReactNode
  onSelect: () => void
  icon?: IconName
  /** Необратимое: красным, в конце меню за чертой, с подтверждением у вызывающего. */
  danger?: boolean
  /**
   * Пункт виден, но недоступен — с причиной словами (`.menu-item[aria-disabled]` + `.why`,
   * CompExtra.dc.html). Для доменных запретов, а не для прав: без права пункт не передают
   * вовсе, а «последнего суперадмина заблокировать нельзя» надо сказать, а не спрятать.
   */
  disabledReason?: ReactNode
}

export interface RecordHeaderProps {
  crumbs?: Crumb[]
  title: ReactNode
  /** Части мета-строки; между ними компонент ставит разделитель «·». */
  meta?: ReactNode[]
  primary?: HeaderAction | null
  secondary?: HeaderAction[]
  menu?: MenuAction[]
  /** Подпись кнопки «⋯» для экранного диктора. */
  menuLabel?: string
}

export function RecordHeader({
  crumbs,
  title,
  meta,
  primary,
  secondary = [],
  menu = [],
  menuLabel,
}: RecordHeaderProps) {
  const { t } = useApp()
  const visibleMeta = (meta ?? []).filter((part) => part != null && part !== undefined && part !== false)
  return (
    <>
      {crumbs && crumbs.length > 0 && (
        <nav className="crumbs" aria-label={t('record.crumbs')}>
          {crumbs.map((crumb, index) => (
            <Fragment key={index}>
              {index > 0 && <span aria-hidden="true">/</span>}
              {crumb.to ? (
                <Link to={crumb.to} className={crumb.mono ? 'mono' : undefined}>
                  {crumb.label}
                </Link>
              ) : (
                <span className={crumb.mono ? 'mono' : undefined} aria-current="page">
                  {crumb.label}
                </span>
              )}
            </Fragment>
          ))}
        </nav>
      )}
      <div className="rec-head">
        <div className="rec-title">
          <h1 className="h1">{title}</h1>
          {visibleMeta.length > 0 && (
            <div className="rec-meta">
              {visibleMeta.map((part, index) => (
                <Fragment key={index}>
                  {index > 0 && (
                    <span className="sep-dot" aria-hidden="true">
                      ·
                    </span>
                  )}
                  {part}
                </Fragment>
              ))}
            </div>
          )}
        </div>
        {(primary || secondary.length > 0 || menu.length > 0) && (
          <div className="rec-actions">
            {secondary.map((action) => (
              <ActionButton key={action.id} action={action} variant="btn-secondary" />
            ))}
            {primary && <ActionButton action={primary} variant="btn-primary" />}
            {menu.length > 0 && <MoreMenu items={menu} label={menuLabel} />}
          </div>
        )}
      </div>
    </>
  )
}

function ActionButton({ action, variant }: { action: HeaderAction; variant: string }) {
  return (
    <button type="button" className={`btn ${variant}`} disabled={action.disabled} onClick={action.onClick}>
      {action.icon && <Icon name={action.icon} size="sm" />}
      {action.label}
      {action.kbd && <span className="kbd">{action.kbd}</span>}
    </button>
  )
}

/**
 * Меню «⋯».
 *
 * Esc и щелчок мимо закрывают, стрелки ходят по пунктам, первый пункт получает фокус при
 * открытии — меню, до которого не добраться с клавиатуры, существует только для мыши.
 */
export function MoreMenu({ items, label, compact }: { items: MenuAction[]; label?: string; compact?: boolean }) {
  const { t } = useApp()
  const [open, setOpen] = useState(false)
  // Compact menus live in table rows, and `.tbl-wrap` scrolls (overflow: auto) — an absolutely
  // positioned popover would be clipped by it on the last rows. Pinned to the viewport instead,
  // and closed on scroll, where a fixed popover would drift away from its row.
  const [anchor, setAnchor] = useState<{ top: number; right: number } | null>(null)
  const rootRef = useRef<HTMLDivElement>(null)
  const buttonRef = useRef<HTMLButtonElement>(null)
  const menuId = useId()

  const regular = items.filter((item) => !item.danger)
  const dangerous = items.filter((item) => item.danger)

  useEffect(() => {
    if (!open) return
    rootRef.current?.querySelector<HTMLElement>('[role="menuitem"]')?.focus()
    const onPointer = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onPointer)
    const onScroll = (event: Event) => {
      if (compact && !rootRef.current?.contains(event.target as Node)) setOpen(false)
    }
    window.addEventListener('scroll', onScroll, true)
    window.addEventListener('resize', onScroll)
    return () => {
      document.removeEventListener('mousedown', onPointer)
      window.removeEventListener('scroll', onScroll, true)
      window.removeEventListener('resize', onScroll)
    }
  }, [open, compact])

  const close = () => {
    setOpen(false)
    buttonRef.current?.focus()
  }

  const onKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Escape') {
      event.stopPropagation()
      close()
      return
    }
    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return
    event.preventDefault()
    const entries = Array.from(rootRef.current?.querySelectorAll<HTMLElement>('[role="menuitem"]') ?? [])
    const at = entries.indexOf(document.activeElement as HTMLElement)
    const step = event.key === 'ArrowDown' ? 1 : -1
    entries[(at + step + entries.length) % entries.length]?.focus()
  }

  const select = (item: MenuAction) => {
    // Focus goes back to the trigger first: a confirm dialog opened by the item
    // remembers it as the place to return to.
    close()
    item.onSelect()
  }

  const renderItem = (item: MenuAction) =>
    item.disabledReason ? (
      <div key={item.id} role="menuitem" className="menu-item" aria-disabled="true" tabIndex={-1}>
        {item.icon && <Icon name={item.icon} />}
        <span className="stack">
          <span>{item.label}</span>
          <span className="why">{item.disabledReason}</span>
        </span>
      </div>
    ) : (
    <button
      key={item.id}
      type="button"
      role="menuitem"
      className={item.danger ? 'menu-item danger' : 'menu-item'}
      onClick={() => select(item)}
    >
      {item.icon && <Icon name={item.icon} />}
      {item.label}
    </button>
    )

  return (
    <div className="rec-menu" ref={rootRef} onKeyDown={onKeyDown}>
      <button
        ref={buttonRef}
        type="button"
        className={compact ? 'btn btn-ghost btn-sm btn-icon' : 'btn btn-secondary btn-icon'}
        aria-label={label ?? t('record.more')}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        onClick={() => {
          if (compact && buttonRef.current) {
            const rect = buttonRef.current.getBoundingClientRect()
            setAnchor({ top: rect.bottom + 4, right: Math.max(8, window.innerWidth - rect.right) })
          }
          setOpen((value) => !value)
        }}
      >
        <Icon name="more" className="i-dots" />
      </button>
      {open && (
        <div
          className={compact ? 'pop pop-row' : 'pop'}
          role="menu"
          id={menuId}
          aria-label={label ?? t('record.more')}
          style={compact && anchor ? { position: 'fixed', top: anchor.top, right: anchor.right } : undefined}
        >
          {regular.map(renderItem)}
          {regular.length > 0 && dangerous.length > 0 && <div className="menu-sep" role="separator" />}
          {dangerous.map(renderItem)}
        </div>
      )}
    </div>
  )
}
